import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Header from '../Component/Header'
import Footer from '../Component/footer'
import PageHead from '../Component/pageHead'
import { PostAction } from '../Component/PostAction'
import { useFormik } from 'formik'
import {
  applyLoginResponse,
  loginPath,
  safeReturnPath,
} from '../utils/sessionAuth'

function normalizeIndianMobile(value) {
  let digits = String(value || '').replace(/\D/g, '')
  if (digits.length === 13 && digits.startsWith('091')) {
    digits = digits.slice(3)
  } else if (digits.length === 12 && digits.startsWith('91')) {
    digits = digits.slice(2)
  } else if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1)
  }
  return /^[6-9]\d{9}$/.test(digits) ? digits : ''
}

function normalizePan(value) {
  return String(value || '').trim().toUpperCase()
}

function isValidPan(value) {
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalizePan(value))
}

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  password: '',
  confirmPassword: '',
  panNumber: '',
  address1: '',
}

function SignUp() {
  const history = useHistory()
  const location = useLocation()
  const returnUrl = safeReturnPath(new URLSearchParams(location.search).get('returnUrl'))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem('id') && sessionStorage.getItem('is_guest') !== '1') {
      history.replace(returnUrl || '/')
    }
  }, [history, returnUrl])

  const { values, handleChange, handleSubmit } = useFormik({
    initialValues: EMPTY,
    onSubmit: (submitValues) => {
      const firstName = String(submitValues.firstName || '').trim()
      const lastName = String(submitValues.lastName || '').trim()
      const email = String(submitValues.email || '').trim()
      const mobileRaw = String(submitValues.mobile || '').trim()
      const mobile = mobileRaw ? normalizeIndianMobile(mobileRaw) : ''
      const password = String(submitValues.password || '')
      const confirmPassword = String(submitValues.confirmPassword || '')
      const address1 = String(submitValues.address1 || '').trim()
      const panNumber = normalizePan(submitValues.panNumber)

      if (!firstName || !lastName) {
        alert('Please enter your first and last name.')
        return
      }
      if (!email && !mobile) {
        alert('Please enter an email address or a mobile number.')
        return
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        alert('Please enter a valid email address.')
        return
      }
      if (mobileRaw && !mobile) {
        alert('Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.')
        return
      }
      if (!password) {
        alert('Please enter a password.')
        return
      }
      if (password !== confirmPassword) {
        alert('Password and confirm password do not match.')
        return
      }
      if (!panNumber) {
        alert('Please enter your PAN number.')
        return
      }
      if (!isValidPan(panNumber)) {
        alert('Enter a valid PAN number (e.g. ABCDE1234F).')
        return
      }
      if (!address1) {
        alert('Please enter your address.')
        return
      }

      const payload = {
        firstName,
        lastName,
        email,
        mobile,
        password,
        panNumber,
        address1,
      }

      const submit = async () => {
        setSaving(true)
        const res = await PostAction('post', payload, 'user/signupUser')
        if (res?.status !== 'success') {
          setSaving(false)
          alert(res?.message || res?.data?.message || 'Sign up failed.')
          return
        }

        const loginRes = await PostAction(
          'post',
          { username: email || mobile, password },
          'user/loginCheck'
        )
        setSaving(false)
        if (applyLoginResponse(loginRes)) {
          history.push(returnUrl || '/')
          return
        }
        alert('Account created. Please log in.')
        history.push(loginPath(returnUrl))
      }
      submit()
    },
  })

  return (
    <>
      <Header />
      <PageHead title="Sign Up" />
      <section className="donation-page">
        <div className="container">
          <div className="donation-card">
            <div className="donation-card-header">
              <h2 className="donation-card-title">Sign Up</h2>
              <p className="donation-card-subtitle">
                Create your account with the details below. Enter an email address, a mobile number,
                or both. At least one is required, and each can belong to only one account.
              </p>
            </div>

            <Form onSubmit={handleSubmit} className="donation-form">
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="firstName">
                    First name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="firstName"
                    id="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="lastName"
                    id="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    autoComplete="email"
                    placeholder="name@example.com"
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="mobile">
                    Mobile
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="mobile"
                    id="mobile"
                    value={values.mobile}
                    onChange={handleChange}
                    autoComplete="tel"
                    inputMode="numeric"
                    placeholder="10-digit mobile"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="password">
                    Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    id="password"
                    value={values.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="confirmPassword">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    id="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="panNumber">
                  PAN Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="panNumber"
                  id="panNumber"
                  value={values.panNumber}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  maxLength={10}
                  placeholder="ABCDE1234F"
                  autoComplete="off"
                  style={{ textTransform: 'uppercase' }}
                />
                <div className="form-text">Required. Format: ABCDE1234F</div>
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="address1">
                  Address
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="address1"
                  id="address1"
                  value={values.address1}
                  onChange={handleChange}
                />
              </div>

              <div className="donation-actions">
                <button className="btn donation-submit-btn" type="submit" disabled={saving}>
                  {saving ? 'Creating account…' : 'Sign Up'}
                </button>
                <p className="text-center mt-3 mb-0" style={{ fontSize: 15 }}>
                  Already have an account? <Link to={loginPath(returnUrl)}>Login</Link>
                </p>
              </div>
            </Form>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default SignUp
